import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { VerticalAppliedFilters } from '@plato/ui-legacy/components/filters/vertical-applied-filters';
import { VerticalFilters } from '@plato/ui-legacy/components/filters/vertical-filters';
import { ErrorBadge } from '@plato/ui-legacy/components/ui/error-badge';
import { Page } from '@plato/ui-legacy/components/ui/page';
import { PageHeader } from '@plato/ui-legacy/components/ui/page-header';
import { useQueryParam } from '@plato/ui-legacy/lib/hooks/use-query-param';
import { stringParam } from '@plato/ui-legacy/lib/query-params';
import { Activity } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityFeedModelType } from '../../../server/types/customer-activity-feed-types.ts';
import { cleanEmpty } from '../../../utils/clean-empty.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { getUsedFilterEventPayload } from '../../analytics/utils/filter-analytics.ts';
import { NoDocumentState } from '../../shared/components/no-document-state/no-document-state.tsx';
import { useActivityFeed } from '../hooks/use-activity-feed.ts';
import type { ActivityFeedLinkSource } from '../hooks/use-activity-filters.ts';
import {
    useActivityFilters,
    useCustomerFilter,
    useCustomerStatusFilter,
    useInsightOutcomeFilter,
    useNoteTypeFilter,
    useTaskStatusFilter,
    useUserFilter,
} from '../hooks/use-activity-filters.ts';
import { ActivityFeedTimeline } from './activity-feed-timeline.tsx';
import { ActivityModelTypeSelect } from './activity-model-type-select.tsx';

const MODEL_TYPE_OPTIONS = [
    ActivityFeedModelType.NOTE,
    ActivityFeedModelType.TASK,
    ActivityFeedModelType.INSIGHT_OUTCOME,
    ActivityFeedModelType.CUSTOMER_STATUS_CHANGE,
];

/**
 * ALL renders every activity the caller's ACL allows (the manager cockpit's
 * "Activities" page). OWN pins the feed to the signed-in user, so the customer
 * filter can no longer widen it to colleagues' activities ("My Activity").
 */
export const ActivityFeedScope = {
    ALL: 'ALL',
    OWN: 'OWN',
} as const;

export type ActivityFeedScope = (typeof ActivityFeedScope)[keyof typeof ActivityFeedScope];

type GlobalActivityFeedProps = {
    scope: ActivityFeedScope;
};

export function GlobalActivityFeed({ scope }: GlobalActivityFeedProps): React.JSX.Element {
    const { t } = useTranslation();
    const sendEvent = useAnalytics();
    const { userId, defaultCurrency } = useAppConfig();
    const { defaultFilters, appliedFilters, setAppliedFilters } = useActivityFilters();

    const isOwnScope = scope === ActivityFeedScope.OWN;

    const [stagedFilters, setStagedFilters] = useState(appliedFilters);

    const selectedCustomerIds = useMemo(
        () => [...new Set([...(appliedFilters.customerIds ?? []), ...(stagedFilters.customerIds ?? [])])],
        [appliedFilters.customerIds, stagedFilters.customerIds],
    );

    const { isLoading, fetchNextPage, hasNextPage, timeGroupedData, isFetchingNextPage, error } =
        useActivityFeed({
            modelTypes: MODEL_TYPE_OPTIONS,
            userIds: isOwnScope ? [userId] : undefined,
        });

    const [source] = useQueryParam('source', stringParam<ActivityFeedLinkSource>(), 'direct');

    useEffect(() => {
        sendEvent('global_activity_feed_viewed', { source });
    }, [sendEvent, source]);

    const customerFilter = useCustomerFilter(selectedCustomerIds);
    const userFilter = useUserFilter();

    const filters = [
        customerFilter,
        isOwnScope ? undefined : userFilter,
        useTaskStatusFilter(),
        useNoteTypeFilter(),
        useInsightOutcomeFilter(),
        useCustomerStatusFilter(),
    ];

    const applyFilters = (values: typeof appliedFilters): void => {
        const next = cleanEmpty(values);
        const filterPayload = getUsedFilterEventPayload(next);
        if (filterPayload.filters.length > 0) {
            sendEvent('global_activity_feed_filter_used', filterPayload);
        }
        setAppliedFilters(next);
    };

    const modelTypeSelect = (
        <ActivityModelTypeSelect
            value={appliedFilters.modelType}
            onChange={(modelType) => {
                applyFilters({ ...appliedFilters, modelType });
            }}
            options={MODEL_TYPE_OPTIONS}
        />
    );

    const isEmpty = !isLoading && timeGroupedData.size === 0;

    return (
        <Page>
            <div className="sticky top-0 z-10 -mb-3 flex flex-col gap-3 bg-white pb-3">
                <div className="flex flex-col gap-3 md:flex-row md:justify-between md:items-center">
                    <PageHeader
                        icon={Activity}
                        title={isOwnScope ? t('page_title__my_activity') : t('page_title__activities')}
                        subTitle={
                            isOwnScope ? t('page_sub_title__my_activity') : t('page_sub_title__activities')
                        }
                    />
                    <VerticalFilters
                        defaultCurrency={defaultCurrency}
                        filters={filters}
                        isLoading={false}
                        searchFilterKey={'searchText'}
                        searchPlaceholder={t('search_placeholder__global', { ns: 'activity-feed' })}
                        defaultFilters={defaultFilters}
                        appliedFilters={appliedFilters}
                        onApplyFilters={applyFilters}
                        onFiltersChange={setStagedFilters}
                        rightSlot={modelTypeSelect}
                    />
                </div>
                <VerticalAppliedFilters
                    defaultCurrency={defaultCurrency}
                    filters={filters}
                    defaultFilters={defaultFilters}
                    appliedFilters={appliedFilters}
                    onApplyFilters={applyFilters}
                />
            </div>
            <section className="flex flex-1 pt-5 mx-auto w-full flex-col gap-3 max-w-4xl">
                <ActivityFeedTimeline
                    isLoading={isLoading}
                    fetchNextPage={() => void fetchNextPage()}
                    hasNextPage={hasNextPage}
                    timeGroupedData={timeGroupedData}
                    isFetchingNextPage={isFetchingNextPage}
                    groupByCustomer
                    source="activities_page"
                />
                {isEmpty && (
                    <NoDocumentState>
                        <NoDocumentState.Title>{t('empty_table__no_results')}</NoDocumentState.Title>
                    </NoDocumentState>
                )}
                {!isFetchingNextPage && error?.message && <ErrorBadge>{error.message}</ErrorBadge>}
            </section>
        </Page>
    );
}
